import { useCallback, useEffect, useMemo, useState } from "react";
import "./schedule-report.scss";
import { Add } from "@mui/icons-material";

import Fallback from "../fallback/fallback";
import Button from "../button/button";
import MaterialTable from "../material-table/material-table";
import Toast from "../toast/toast";

import ConfirmPopUp from "../confirm-modal/confirm-modal";
import logo from "../../assets/images";
import { Box, IconButton } from "@mui/material";
import { Edit, Trash } from "../icons";

import ScheduleReportModal from "../schedule-report/schedule-report-modal";
import { getErrorMessage, getToken } from "../../utils/common";
import { ScheduleReportRow } from "./utils/type"
import { columns } from "./utils/constant"
import ErpLoader from "../loaders/erp-loader";
import Typography from "../typography/typography";
import Footer from "../footer/footer";
import { transformTableColumns } from "../../utils/common-utility";
import { useTranslation } from "react-i18next";
import Chip from "../chip/chip";
import { deleteScheduleReportType, getScheduleReportType } from "./utils/common";
interface IToast {
  type: "normal" | "alert";
  message: string;
}

const GeneralScheduleReport = ({ module = 'accounting', title = 'Schedule report' }: { module: string, title: string }) => {

  //states
  const [pagination, setPagination] = useState<any>({
    pageNo: 1,
    limit: 20,
    totalCount: 0
  })
  const [toast, setToast] = useState<IToast | null>(null);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [openScheduleModal, setOpenScheduleModal] =
    useState<ScheduleReportRow | null>(null);
  const u_data = localStorage.getItem("_u_data") || undefined;
  const userData = u_data ? JSON.parse(u_data) : "";

  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [scheduleReportToDelete, setScheduleReportToDelete] =
    useState<ScheduleReportRow | null>(null);

  const [tableRows, setRows] = useState<ScheduleReportRow[]>([])
  const limit = pagination?.limit;
  const total = limit ? Math.ceil(pagination?.totalCount / limit) : 0;
  const skip = limit * (pagination?.pageNo - 1) || 0
  const { t } = useTranslation()

  //transform table columns
  const renderEmails = (value: string | undefined | null) => {
    const emails = value?.split(',');
    return emails ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {emails.map((item: string) => (
          <Chip
            key={item}
            type='normal'
            label={
              <Typography type='s4' weight='medium' color={'inherit'}>
                {item}
              </Typography>
            }
          />
        ))}
      </Box>
    ) : '-';
  };

  const tableColumns = useMemo(
    () =>
      transformTableColumns({
        columns: columns,
        currencySymbol: undefined,
        translationFn: t,
        enableFooter: true,
        redirectionLink: undefined,
        redirectionLinkState: undefined,
        rows: tableRows,
        statusClasses: `scheduleReport--StatusChip--`,
        customizeValue: (row, columnAccessorKey, value) => {
          switch (columnAccessorKey) {
            case 'emails':
              return renderEmails(value);
            // Add other cases here if needed
            default:
              return value;
          }
        },
      }),
    //eslint-disable-next-line
    [columns, tableRows, t]
  );

  const getScheduleReport = useCallback(
    async () => {
      try {
        setIsLoading(true)
        const { data = {}, pagination: paginationData } = await getScheduleReportType({
          ...getToken(),
          skip,
          limit,
          filters: `(type.eq=${module}&created_by.eq=${userData?.id})`,
        })
        setRows(data?.schedulers)
        setPagination(prev => ({ ...prev, ...paginationData }))

      } catch (error) {
        const errorMessage = getErrorMessage(error?.message)
        setToast({
          type: "alert",
          message: errorMessage,
        })
      } finally {
        setIsLoading(false)
        setInitialLoading(false)
      }
      //eslint-disable-next-line
    },
    [limit, skip, module, setPagination]
  );

  const handleScheduleReportDelete = async () => {
    try {
      if (scheduleReportToDelete) {
        setIsDeleting(true)
        await deleteScheduleReportType({
          ...getToken(),
          id: scheduleReportToDelete.id
        })
        setToast({
          type: "normal",
          message: "Report schedule deleted successfully",
        });
        getScheduleReport();
        setScheduleReportToDelete(null);
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error?.message)
      setToast({
        type: "alert",
        message: errorMessage,
      })
    } finally {
      setIsDeleting(false)
    }
  };

  const handleUpdate = useCallback(async () => {
    getScheduleReport();
    //eslint-disable-next-line
  }, []);



  useEffect(() => {
    getScheduleReport();
  }, [getScheduleReport]);



  const handleAddButton = () => setOpenScheduleModal(true);
  const handlePaginationModel = (model: IPaginationModel) => {
    setPagination(model)
  }

  return (
    <section className="scheduleReport">

      <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
        <Typography type="h3" weight="bold" color={'inherit'}>{title}</Typography>
        {/* <Button
        variant="outlined"
        className="scheduleReport__addBtn"
        startIcon={<Add />}
        onClick={handleAddButton}
    >Schedule Report</Button> */}
      </Box>

      {initialLoading ? (
        <ErpLoader />
      ) : (
        <>
          {tableRows?.length > 0 ? (
            <>
              <MaterialTable
                rows={tableRows || []}
                columns={tableColumns}
                totalPages={0}
                columnOrder={["mrt-row-select"]}
                states={{ isLoading }}
                enableColumnDragging={true}
                enableEditing={false}
                isResetRow={false}
                enableRowSelection={false}
                paginationModel={{ pageNo: 1, limit: 20 }}
                defaultActionColumnItems={[
                  <IconButton
                    onClick={({ row }: any) =>
                      setOpenScheduleModal(row?.original)
                    }
                  >
                    <Edit fontSize="small" htmlColor="#292D32" />
                  </IconButton>,
                  <IconButton
                    onClick={({ row }: any) =>
                      setScheduleReportToDelete(row?.original)
                    }
                  >
                    <Trash fontSize="small" htmlColor="#292D32" />
                  </IconButton>,
                ]}
              />
              <Footer
                total={total}
                paginationModel={{
                  ...pagination,
                  limit: pagination?.limit
                }}
                handlePaginationModel={handlePaginationModel}
                resource='purchase-request'
                enablePages={false}
              />
            </>
          ) : (
            <>
              {!tableRows.length && (
                <Fallback
                  heading="No schedule report"
                  subHeading="Add schedule report"
                  icon={<img src={logo.reportsFallback} />}
                >
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddButton}
                  >
                    Add
                  </Button>
                </Fallback>
              )}
            </>
          )}
        </>
      )}

      <Toast
        open={Boolean(toast)}
        type={toast?.type}
        message={toast?.message}
        handleClose={() => setToast(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      />

      <ScheduleReportModal
        isOpen={Boolean(openScheduleModal)}
        onClose={() => setOpenScheduleModal(null)}
        itemData={openScheduleModal}
        onSubmit={handleUpdate}
        module={module}
      />



      <ConfirmPopUp
        open={Boolean(scheduleReportToDelete)}
        onClose={() => setScheduleReportToDelete(null)}
        onConfirm={handleScheduleReportDelete}
        title="Delete schedule report"
        loading={isDeleting}
        description={`Are you sure you want to delete schedule report: ${scheduleReportToDelete?.name} ?`}
      />
    </section>
  );
};

export { GeneralScheduleReport };
export default GeneralScheduleReport;
